import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Checkbox, ModalFooter, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { FeatureFlags } from '../../../../../components/Authorizer';
import FormInput from '../../../../../components/form/FormInput';
import { inputTypes } from '../../../../../components/form/FormInput/util';
import StackedButtonGroup from '../../../../../components/Modal/ModalButtonGroups/StackedButtonGroup';
import { analyticsIds } from '../../../../../constants/analyticsIds';
import { getValidationObject } from '../../../../../constants/regexPatterns';
import { selectCompanyFeatures } from '../../../../../redux/modules/company';
import { insuredSearch } from '../../../../../redux/modules/insureds';
import { hideModal } from '../../../../../redux/modules/modal';
import { selectReportsCheckAllChecked, selectReportsChecked, sendExportReportExcelEmail } from '../../../../../redux/modules/reports';
import { selectUser } from '../../../../../redux/modules/user';

const ExportReportModal = ({ searchParams }) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const companyFeatures = useSelector(selectCompanyFeatures);
  const checked = useSelector(selectReportsChecked);
  const checkAllChecked = useSelector(selectReportsCheckAllChecked);

  const { total: searchTotal } = useSelector(insuredSearch.selectPageInfo);

  const [includeRequirementsAndPolicies, setIncludeRequirementsAndPolicies] = useState(false);
  const [groupByCodes, setGroupByCodes] = useState(false);

  const itemsEnabled = useMemo(() => companyFeatures.find(cf => cf.name === FeatureFlags.Items)?.isEnabled, [companyFeatures]);

  const useFormMethods = useForm({ defaultValues: { email: user.userEmail } });

  const { handleSubmit } = useFormMethods;

  const timeEstimate = useMemo(() => {
    let reportTotal = 1;

    if (!checkAllChecked && Object.keys(checked).length) {
      reportTotal = Object.values(checked).length;
    } else {
      reportTotal = searchTotal;
    }

    if (!reportTotal) {
      return 'Calculating...';
    }

    let dividend = 200;
    let rounder = 5;
    let upperIncrement = 5;

    if (itemsEnabled || includeRequirementsAndPolicies) {
      dividend = 50;
      rounder = 10;
      upperIncrement = 10;
    }

    const total = parseInt(reportTotal, 10);
    const itemsPerMinute = Math.ceil(total / dividend);
    let estimate;

    if (itemsPerMinute <= 5) {
      estimate = '1-5';
    } else {
      const lowerLimit = Math.round(itemsPerMinute / rounder) * rounder;
      const upperLimit = Math.round((itemsPerMinute + upperIncrement) / rounder) * rounder;

      estimate = `${lowerLimit}-${upperLimit}`;
    }

    return `*It may take a few minutes for the report to reach your inbox (~${estimate} min).  Larger reports will be sent in batches.`;
  }, [checkAllChecked, checked, searchTotal, itemsEnabled, includeRequirementsAndPolicies]);

  const onSubmit = data => {
    dispatch(sendExportReportExcelEmail(
      data.email,
      data.cc,
      searchParams,
      itemsEnabled,
      includeRequirementsAndPolicies,
      groupByCodes
    ));

    dispatch(hideModal());
  };

  return (
    <>
      <Box>
        <form id="export-report-form" onSubmit={handleSubmit(onSubmit)}>
          <Text mb={4}>Who would you like to send this report to?</Text>
          <FormInput
            dataAnalyticsId={analyticsIds.reportsPage.excelReportRecipients}
            inputName="email"
            labelName="To:"
            mt={2}
            placeholder="To:"
            type={inputTypes.standard}
            useFormMethods={useFormMethods}
            validationRules={({
              required: { value: true, message: 'At least one email is required' },
              ...getValidationObject('commaSeparatedEmailAddress')
            })}
          />
          <FormInput
            dataAnalyticsId={analyticsIds.reportsPage.excelReportCcRecipients}
            inputName="cc"
            labelName="cc:"
            mt={2}
            placeholder="cc:"
            type={inputTypes.standard}
            useFormMethods={useFormMethods}
            validationRules={getValidationObject('commaSeparatedEmailAddress')}
          />
          {!itemsEnabled && (
            <Checkbox
              isChecked={includeRequirementsAndPolicies}
              mb={6}
              onChange={() => setIncludeRequirementsAndPolicies(!includeRequirementsAndPolicies)}
            >
              Include Requirements and Policies
            </Checkbox>
          )}
          {itemsEnabled && (
          <Checkbox
            isChecked={groupByCodes}
            mb={6}
            onChange={() => setGroupByCodes(!groupByCodes)}
          >
            Group By Unit Codes
          </Checkbox>
          )}
          <Text
            fontSize="sm"
            fontStyle="italic"
          >
            {timeEstimate}
          </Text>
        </form>
      </Box>
      <ModalFooter>
        <StackedButtonGroup
          dataAnalyticsId={analyticsIds.reportsPage.confirmExportReportButton}
          label="Export Report"
          onClick={handleSubmit(onSubmit)}
        />
      </ModalFooter>
    </>

  );
};

ExportReportModal.propTypes = {
  searchParams: PropTypes.shape({
    query: PropTypes.objectOf(PropTypes.object),
    sort: PropTypes.arrayOf(PropTypes.object),
    source: PropTypes.arrayOf(PropTypes.object)
  })
};

export default ExportReportModal;
